import { Button, Card, CardBody, DataTable, Icon, Modal, Paragraph, highlightText, matchesExactKey, Badge } from '@aidkitorg/component-library';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useLocation, NavLink } from 'react-router-dom';
import { useSpinDelay } from 'spin-delay';
import { Report, useReports } from '../../../Contexts/ReportsContext';
import { useAsyncEffect } from '../../../Hooks/AsyncEffect';
import { useLocalizedStrings } from '../../../Localization';
import { useCopyToClipboard } from '../../../Hooks/CopyToClipboard';

export const ReportsDataTable = () => {
  const L = useLocalizedStrings()
  const history = useHistory()
  const location = useLocation()

  const {
    reports,
    isReportsLoading,
    isReportsSearching,
    getReports,
    deleteReportById,
    reportsTotal,
    reportsPage,
    reportsLimit,
    reportsTotalPages,
    reportsSearch,
    reportsSearchDisplay,
    reportsSorting,
    onReportsPaginationChange,
    onReportsSearchChange,
    onReportsSortingChange
  } = useReports()

  // Track if initial data has been loaded
  const [hasInitialLoad, setHasInitialLoad] = useState(false)

  // Use spin-delay to prevent loading flashes - only show loading after 250ms
  const showLoadingSpinner = useSpinDelay(isReportsLoading && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });
  
  const showSearchSpinner = useSpinDelay(isReportsSearching && hasInitialLoad, {
    delay: 250,
    minDuration: 150
  });

  // URL parameter utilities
  const getUrlSearchParam = (key: string): string => {
    const params = new URLSearchParams(location.search)
    return params.get(key) || ''
  }

  const setUrlSearchParam = useCallback((key: string, value: string) => {
    const params = new URLSearchParams(location.search)
    if (value.trim()) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    const newSearch = params.toString()
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`
    history.replace(newUrl)
  }, [location.pathname, location.search, history])

  // Enhanced search change handler with URL encoding
  const handleSearchChange = useCallback((search: string) => {
    setUrlSearchParam('search', search) // Update URL immediately
    onReportsSearchChange(search) // Call the original debounced handler
  }, [setUrlSearchParam, onReportsSearchChange])

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [reportToShare, setReportToShare] = useState<Report | null>()
  const { copied, copyToClipboard } = useCopyToClipboard()

  useAsyncEffect(async () => {
    // Initialize search from URL on first load
    const urlSearch = getUrlSearchParam('search')
    if (urlSearch && urlSearch !== reportsSearchDisplay) {
      // Use the original search change handler to properly initialize state
      onReportsSearchChange(urlSearch)
    } else {
      await getReports()
    }
    setHasInitialLoad(true)
  }, []);



  const handleShareClick = (report: Report) => {
    setReportToShare(report)
    setShareModalOpen(true)
  }

  const handleDeleteClick = (report: Report) => {
    setReportToDelete(report)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = useCallback(async () => {
    if (!reportToDelete) return

    setIsDeleting(true)
    try {
      await deleteReportById(reportToDelete.id)
      setDeleteModalOpen(false)
      setReportToDelete(null)
    } catch (error) {
      // Error handling is done in the context
    } finally {
      setIsDeleting(false)
    }
  }, [reportToDelete, deleteReportById])

  const handleCancelDelete = () => {
    setDeleteModalOpen(false)
    setReportToDelete(null)
  }

  const REPORTS_COLUMNS: ColumnDef<Report>[] = React.useMemo(() => [
    {
      accessorKey: 'title',
      header: L.admin_panel.reports.report_name,
      enableSorting: true,
      cell: ({ row }) => {
        const reportId = row.original.id
        const isDraft = row.original.status === 'draft'
        return (
          <div className="flex items-center gap-2">
            <NavLink
              to={`/admin-panel/reports/${reportId}`}
              className="font-medium text-blue-600 hover:text-blue-800"
            >
              {(!isReportsSearching && reportsSearchDisplay && reportsSearchDisplay === reportsSearch) ? highlightText(row.original.title, reportsSearchDisplay) : row.original.title}
            </NavLink>
            {isDraft && (
              <Badge variant="info">
                Draft
              </Badge>
            )}
          </div>
        )
      }
    },
    {
      accessorKey: 'description',
      header: L.admin_panel.reports.description,
      enableSorting: true, // Enable description sorting
      cell: ({ row }) => (
        <span className="text-gray-700">
          {(!isReportsSearching && reportsSearchDisplay && reportsSearchDisplay === reportsSearch) ? highlightText(row.original.description || '', reportsSearchDisplay) : (row.original.description || '')}
        </span>
      )
    },
    {
      accessorKey: 'updated_at',
      header: L.admin_panel.reports.updated_on,
      enableSorting: true,
      cell: ({ row }) => {
        if (!row.original.updated_at) return "--"
        const date = new Date(row.original.updated_at)
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date)

        return <span>{formatted}</span>
      },
    },
    {
      accessorKey: 'created_by_name',
      header: L.admin_panel.reports.created_by,
      enableSorting: true,
      cell: ({ row }) => (
        <span>
          {(!isReportsSearching && reportsSearchDisplay && reportsSearchDisplay === reportsSearch) ? highlightText(row.original.created_by_name || '', reportsSearchDisplay) : (row.original.created_by_name || '')}
        </span>
      )
    }
  ], [L, isReportsSearching, reportsSearchDisplay, reportsSearch])

  const reportActions: Array<TableAction<Report>> = React.useMemo(() => [
    {
      label: L.admin_panel.reports.view,
      onClick: (row: Report) => {
        history.push(`/admin-panel/reports/${row.id}`)
      },
      icon: 'EyeIcon'
    },
    {
      label: L.admin_panel.reports.export,
      onClick: (row: Report) => { },
      icon: 'ArrowDownTrayIcon',
      hideWhen: (row: Report) => row.status === 'draft'
    },
    {
      label: L.admin_panel.reports.share,
      onClick: (row: Report) => {
        handleShareClick(row)
      },
      icon: 'LinkIcon',
      hideWhen: (row: Report) => row.status === 'draft'
    },
    {
      label: L.admin_panel.reports.delete,
      onClick: (row: Report) => {
        handleDeleteClick(row)
      },
      icon: 'TrashIcon',
    }
  ], [L, history])

  const reportsEmptyState = (
    <div className="py-2 text-center text-gray-600 flex flex-col gap-y-4">
      <div>
        {L.admin_panel.reports.reports_empty_msg}
      </div>
      <div>
        <Button
          onClick={(e) => {
            history.push('/admin-panel/reports/new')
          }}
        >
          {L.admin_panel.reports.create_new_report}
        </Button>
      </div>
    </div>
  )

  // Only show empty state if we have completed initial load, have no reports, 
  // no active search, and are not currently loading
  const reportsEmpty = hasInitialLoad && 
                      !isReportsLoading && 
                      !isReportsSearching && 
                      reports.length === 0 && 
                      !reportsSearch.trim()

  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not typing in an input/textarea
      if (event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement) {
        return
      }

      // Don't handle if modal is open
      if (deleteModalOpen) {
        return
      }

      // Handle Alt+ArrowLeft for previous page
      if (matchesExactKey(event, { key: 'ArrowLeft', alt: true }) && reportsPage > 1) {
        event.preventDefault()
        onReportsPaginationChange({
          pageIndex: reportsPage - 2, // Convert to 0-based index
          pageSize: reportsLimit
        })
      }

      // Handle Alt+ArrowRight for next page
      if (matchesExactKey(event, { key: 'ArrowRight', alt: true }) && reportsTotalPages && reportsPage < reportsTotalPages) {
        event.preventDefault()
        onReportsPaginationChange({
          pageIndex: reportsPage, // Already correct for 0-based index
          pageSize: reportsLimit
        })
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [reportsPage, reportsTotalPages, reportsLimit, onReportsPaginationChange, deleteModalOpen])

  const handleCopy = async () => {
    const shareLink = `${window.location.origin}/admin-panel/reports/${reportToShare?.id}`
    await copyToClipboard(shareLink);
  };

  const shareModal = (
    <Modal
      size="sm"
      title={L.admin_panel.reports.share_report}
      open={shareModalOpen}
      onClose={() => setShareModalOpen(false)}
    >
      <div>
        <Paragraph>
          {L.admin_panel.reports.share_report_msg}
        </Paragraph>

        <div className="flex justify-center">
          <Button
            icon={
              <Icon
                name={copied ? "CheckIcon" : "LinkIcon"}
                role="decorative"
              />}
            variant="secondary"
            onClick={handleCopy}
          >
            {copied ? L.admin_panel.reports.link_copied : L.admin_panel.reports.copy_link}
          </Button>
        </div>
      </div>
    </Modal>
  )

  const deleteConfirmationModal = (
    <Modal
      title={L.admin_panel.reports.delete_report}
      open={deleteModalOpen}
      onClose={handleCancelDelete}
    >
      <div className="mb-4">
        {L.admin_panel.reports.are_you_sure_delete.replace('{reportName}', reportToDelete?.title ?? '')}
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={handleCancelDelete}
          disabled={isDeleting}
        >
          {L.admin_panel.reports.cancel}
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirmDelete}
          disabled={isDeleting}
        >
          {isDeleting ? L.admin_panel.reports.deleting : L.admin_panel.reports.delete}
        </Button>
      </div>
    </Modal>
  )

  return (
    <>
      <Card className="rounded-md h-full w-full">
        <CardBody>
          {reportsEmpty ? (
            reportsEmptyState
          ) : (
            <div className="relative">
              <DataTable
                columns={REPORTS_COLUMNS}
                actions={reportActions}
                data={reports}
                enableSearch
                searchLabel={L.admin_panel.reports.search_reports}
                dataFilteredOutMessage={reportsSearchDisplay.trim() ? `No reports found matching "${reportsSearchDisplay}"` : L.admin_panel.reports.no_reports}
                initializing={!hasInitialLoad} // Show shimmer state during initial load
                loading={showLoadingSpinner || showSearchSpinner}
                showPagination
                pageSize={reportsLimit}
                manualSearch={{
                  searchValue: reportsSearchDisplay,
                  onSearchChange: handleSearchChange,
                  resetPageOnChange: false // Prevent automatic pagination reset on search
                }}
                manualPagination={{
                  pageCount: reportsTotalPages || 0,
                  pageIndex: Math.max(0, reportsPage - 1), // Convert from 1-based to 0-based, ensure non-negative
                  totalRows: reportsTotal || 0,
                  onPaginationChange: onReportsPaginationChange
                }}
                manualSorting={{
                  sorting: reportsSorting,
                  onSortingChange: onReportsSortingChange
                }}
              />
            </div>
          )}
        </CardBody>
      </Card>
      {shareModal}
      {deleteConfirmationModal}
    </>
  )
}